import React from 'react';
import {
  ApartmentOutlined,
  AppstoreOutlined,
  BankOutlined,
  BarChartOutlined,
  DeleteOutlined,
  DollarOutlined,
  EditOutlined,
  EyeOutlined,
  FlagOutlined,
  GiftOutlined,
  MessageOutlined,
  MinusCircleOutlined,
  NotificationOutlined,
  PlayCircleOutlined,
  PlusCircleOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  StopOutlined,
  SyncOutlined,
  TagsOutlined,
  TeamOutlined,
  UserAddOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { ScopeItem } from '../../services/scopeManagement';

type IconType = React.ComponentType<any>;

type GroupDef = {
  key: string;
  label: string;
  match: string[];
  Icon: IconType;
};

// Order matters: a scope joins the FIRST group whose keyword its name contains.
// Put more specific groups before broader ones (e.g. customer before user).
const GROUP_DEFS: GroupDef[] = [
  { key: 'affiliate', label: 'แนะนำเพื่อน', match: ['AFFILIATE'], Icon: ApartmentOutlined },
  { key: 'deposit', label: 'เติมเงิน', match: ['DEPOSIT'], Icon: PlusCircleOutlined },
  { key: 'withdraw', label: 'ถอน/โอนเงิน', match: ['WITHDRAW'], Icon: MinusCircleOutlined },
  { key: 'customer', label: 'ลูกค้า', match: ['CUSTOMER'], Icon: UserOutlined },
  { key: 'user', label: 'พนักงาน / สิทธิ์', match: ['REGISTER', 'EDIT-USER', 'MANAGE-SCOPE', 'USER'], Icon: TeamOutlined },
  { key: 'bonus', label: 'โบนัส', match: ['BONUS'], Icon: GiftOutlined },
  { key: 'bank', label: 'ธนาคาร', match: ['BANK'], Icon: BankOutlined },
  { key: 'report', label: 'รายงาน', match: ['REPORT'], Icon: BarChartOutlined },
  { key: 'game', label: 'เกม', match: ['GAME'], Icon: PlayCircleOutlined },
  { key: 'wallet', label: 'กระเป๋าเงิน', match: ['WALLET'], Icon: WalletOutlined },
  { key: 'ticket', label: 'Ticket', match: ['TICKET'], Icon: TagsOutlined },
  { key: 'quest', label: 'ภารกิจ', match: ['QUEST'], Icon: FlagOutlined },
  { key: 'realtime', label: 'ตรวจสอบยอดเงิน', match: ['REALTIME'], Icon: SyncOutlined },
  { key: 'payment', label: 'ระบบชำระเงิน', match: ['PAYMENT'], Icon: DollarOutlined },
  { key: 'banner', label: 'ป้ายข้อความ', match: ['BANNER'], Icon: NotificationOutlined },
  { key: 'config', label: 'ตั้งค่ากลุ่มธนาคาร', match: ['CONFIG'], Icon: SettingOutlined },
  { key: 'chat', label: 'แชท / ฝนดาว', match: ['CHAT', 'RAIN'], Icon: MessageOutlined },
];

const OTHER_DEF: GroupDef = { key: 'other', label: 'อื่นๆ', match: [], Icon: AppstoreOutlined };

/**
 * Friendly Thai labels per scope.
 * - Page entries mirror the real menu `linkText` in src/config/routes.ts.
 * - Action/button entries are curated (no central registry exists for buttons).
 * Anything not listed falls back to the raw scope name.
 * NOTE: kept as a plain map (not imported from routesConfig) to avoid a
 * circular import: routes.ts -> EmployeeScope -> scopeGroups -> routes.ts.
 */
const SCOPE_LABELS: { [scopeName: string]: string } = {
  // wildcard
  'ALL': 'ทุกสิทธิ์ (ALL)',

  // pages (mirror routesConfig linkText)
  'DEPOSIT-PAGE': 'เติมเงิน',
  'WITHDRAW-PAGE': 'โอนเงิน',
  'WALLET-PAGE': 'กระเป๋าเงิน/เกมลูกค้า',
  'CUSTOMER-PAGE': 'รายชื่อลูกค้า',
  'REGISTER-PAGE': 'พนักงาน',
  'BANNER-PAGE': 'ป้ายข้อความหน้าเว็บ',
  'BONUS-PAGE': 'โบนัสลูกค้า',
  'TICKET-MANAGEMENT-PAGE': 'Ticket ลูกค้า',
  'REPORT-PAGE': 'รายงานการเดินบัญชี',
  'REALTIME-TRANSFER-PAGE': 'ตรวจสอบยอดเงิน',
  'GAME-PAGE': 'จัดการเกม',
  'PAYMENT-SETTING-PAGE': 'ระบบชำระเงิน',
  'QUEST-MANAGEMENT-PAGE': 'ตั้งค่าภารกิจ',
  'SETTING-CONFIG-PAGE': 'กลุ่มธนาคาร',
  'MANAGE-SCOPE-PAGE': 'จัดการ scope พนักงาน',
  'AFFILIATE_ORGCHART-PAGE': 'องค์กร (Affiliate)',
  'AFFILIATE-OVERVIEW-PAGE': 'ภาพรวมรายได้',
  'AFFILIATE-CAMPAIGN-PAGE': 'แคมเปญ',
  'AFFILIATE-QUEST-CREATE-PAGE': 'เพิ่มเควส',
  'AFFILIATE-QUEST-PROGRESS-PAGE': 'เช็คความคืบหน้าเควส',
  'AFFILIATE-COMMISSION-PAGE': 'ค้นหาคอมมิชชัน',

  // actions / buttons (curated)
  'DEPOSIT-HISTORY-SHOWALL': 'ดูประวัติเติมเงินทั้งหมด',
  'CREATE-DEPOSIT-TRANSACTION': 'เพิ่มรายการเติมเงิน',
  'EDIT-DEPOSIT-TRANSACTION': 'แก้ไขรายการเติมเงิน',
  'DELETE-DEPOSIT-TRANSACTION': 'ลบรายการเติมเงิน',
  'CREATE-WITHDRAW-TRANSACTION': 'เพิ่มรายการถอน/โอน',
  'EDIT-WITHDRAW-TRANSACTION': 'แก้ไขรายการถอน/โอน',
  'DELETE-WITHDRAW-TRANSACTION': 'ลบรายการถอน/โอน',
  'WITHDRAW-HISTORY-SHOWALL': 'ดูประวัติถอน/โอนทั้งหมด',
  'REGISTER-CUSTOMER': 'สมัครสมาชิกลูกค้า',
  'EDIT-CUSTOMER': 'แก้ไขลูกค้า',
  'EDIT-CUSTOMER-STATUS': 'แก้ไขสถานะลูกค้า',
  'REGISTER-BUTTON': 'ปุ่มเพิ่มพนักงาน',
  'REGISTER-USER.OWNER': 'เพิ่มพนักงาน: Owner',
  'REGISTER-USER.DM': 'เพิ่มพนักงาน: DM',
  'REGISTER-USER.ADMIN+': 'เพิ่มพนักงาน: ADMIN+',
  'REGISTER-USER.ADMIN': 'เพิ่มพนักงาน: ADMIN',
  'EDIT-USER.OWNER': 'แก้ไขพนักงาน: Owner',
  'EDIT-USER.DM': 'แก้ไขพนักงาน: DM',
  'EDIT-USER.ADMIN+': 'แก้ไขพนักงาน: ADMIN+',
  'EDIT-USER.ADMIN': 'แก้ไขพนักงาน: ADMIN',
  'MANAGE-BANK': 'จัดการบัญชีธนาคาร',
  'ADD-BANK': 'เพิ่มบัญชีธนาคาร',
  'EDIT-BANK': 'แก้ไขบัญชีธนาคาร',
  'ADD-BONUS': 'เพิ่มโบนัส',
  'EDIT-BONUS': 'แก้ไขโบนัส',
  'ADD-TICKET': 'เพิ่ม Ticket',
  'EDIT-CONFIG': 'แก้ไขการตั้งค่า',
  'AFFILIATE-CAMPAIGN-CREATE-BUTTON': 'ปุ่มสร้างแคมเปญ',
  'AFFILIATE-QUEST-CREATE-BUTTON': 'ปุ่มสร้างเควส',
  'AFFILIATE-QUEST-DETAIL-BUTTON': 'ปุ่มดูรายละเอียดเควส',
  'BAN-CHAT': 'แบนแชท',
  'BAN-RAIN': 'แบนฝนดาว',
};

/** Friendly label for a scope; falls back to the raw scope name if unmapped. */
export const getScopeLabel = (name: string): string => SCOPE_LABELS[name] || name;

export type GroupedScope = {
  key: string;
  label: string;
  Icon: IconType;
  pages: ScopeItem[];
  actions: ScopeItem[];
};

const isPageScope = (name: string): boolean => name.toUpperCase().includes('PAGE');

const findGroupKey = (name: string): string => {
  const upper = name.toUpperCase();
  const def = GROUP_DEFS.find((group) => group.match.some((keyword) => upper.includes(keyword)));
  return def ? def.key : OTHER_DEF.key;
};

/**
 * Group a flat scope list by page/feature using the config above.
 * Within each group, scopes containing "PAGE" become page-access toggles,
 * the rest become action toggles. Empty groups are omitted; group order
 * follows GROUP_DEFS with "อื่นๆ" last.
 */
export const groupScopes = (scopes: ScopeItem[]): GroupedScope[] => {
  const buckets: { [key: string]: ScopeItem[] } = {};
  scopes.forEach((scope) => {
    const key = findGroupKey(scope.name);
    (buckets[key] = buckets[key] || []).push(scope);
  });

  const result: GroupedScope[] = [];
  [...GROUP_DEFS, OTHER_DEF].forEach((def) => {
    const items = buckets[def.key];
    if (!items || items.length === 0) return;
    result.push({
      key: def.key,
      label: def.label,
      Icon: def.Icon,
      pages: items.filter((s) => isPageScope(s.name)),
      actions: items.filter((s) => !isPageScope(s.name)),
    });
  });
  return result;
};

/** Pick an icon for an action/button scope based on its name. */
export const getActionIcon = (name: string): IconType => {
  const upper = name.toUpperCase();
  if (upper.includes('DELETE')) return DeleteOutlined;
  if (upper.includes('EDIT')) return EditOutlined;
  if (upper.includes('CREATE') || upper.includes('ADD') || upper.includes('BUTTON')) return PlusOutlined;
  if (upper.includes('SHOWALL')) return EyeOutlined;
  if (upper.includes('BAN') || upper.includes('STOP')) return StopOutlined;
  if (upper.includes('REGISTER')) return UserAddOutlined;
  if (upper.includes('SCOPE')) return SafetyCertificateOutlined;
  return TagsOutlined;
};
